import { combineLatest, Subscription } from 'rxjs';
import Logger from '../../js/lib/logger';
import StorageService from "../lib/storage.service";
import { EstimateData, PricingData } from '../lib/types';
import { depth$, estimate$, height$, inserts$, subtotal$, trayColor$, width$ } from '../lib/state';
import { DEFAULT_ESTIMATE_DATA } from '../lib/constants';
import { deepClone } from '../lib/util.functions';

class PricingEstimate extends HTMLElement {
  #NAME = 'PricingEstimate';
  #pricingData: PricingData;
  #sub?: Subscription;

  constructor() {
    super();
    this.#pricingData = StorageService.get('pricingData');
    this.#pricingData.trayVariants.sort((a,b) => a.price - b.price);
    Logger.info(`${this.#NAME} > constructor`, this.#pricingData);
    if (!this.#pricingData) {
      const backLink: HTMLAnchorElement | null = document.querySelector('#back-link');
      if (backLink) backLink.click();
    }
  }

  connectedCallback(): void {
    Logger.info(`${this.#NAME} > connectedCallback`);
    this.#sub = combineLatest({
      width: width$,
      height: height$,
      depth: depth$,
      color: trayColor$,
      inserts: inserts$,
    }).subscribe(dta => {
      const estimateData: EstimateData = deepClone(DEFAULT_ESTIMATE_DATA) as EstimateData;
      //
      const filteredVariants = this.#pricingData.trayVariants.filter(obj => {
        return obj.evaluations?.map(e => dta[e.type] >= e.equalsOrMoreThan && dta[e.type] < e.lessThan)
                                        .reduce((a,b) => a ? a : b);
      });
      /* console.log('filteredVariants', filteredVariants); */
      // set tray variant & price
      let trayVariant = filteredVariants[filteredVariants.length - 1];
      if (dta.color) {
        const variant = filteredVariants.find(obj => obj.color === dta.color?.name);
        if (variant){
          trayVariant = variant;
          estimateData.tray.title += ` (${variant.title})`;
        }
      }
      estimateData.tray.id = trayVariant.id;
      estimateData.tray.price = trayVariant.price;
      // add insert charges
      this.#pricingData.insertVariants.forEach(obj => {
        const quantity = dta.inserts.filter(insert => insert.type === obj.type).length;
        if (quantity) {
          estimateData.otherCharges.push({
            id: obj.id,
            title: obj.title as string,
            price: obj.price,
            quantity
          })
        }
      })
      // eval if other charges apply
      this.#pricingData.measurementChargeVariants.forEach(obj => {
        const addCharge = obj.evaluations?.map(e => dta[e.type] >= e.equalsOrMoreThan && dta[e.type] < e.lessThan)
                                        .reduce((a,b) => a ? a : b) || false;
        if (addCharge) {
          estimateData.otherCharges.push({
            id: obj.id,
            title: obj.title,
            price: obj.price
          })
        }
      })
      // emit subtotal
      const subtotal = estimateData.otherCharges.map(c => c.quantity ? c.quantity * c.price : c.price)
                                                .reduce((a,b) => a + b, estimateData.tray.price);
      subtotal$.next(subtotal);
      // emit estimate
      estimate$.next(estimateData);
    });
  }

  disconnectedCallback(): void {
    this.#sub?.unsubscribe();
  }
}

customElements.define('pricing-estimate', PricingEstimate);